import React from "react";

const GitCheck = () => {
  return (
    <>
      <style jsx>{`
        .code-block {
          background-color: #1e1e1e;
          border: 1px solid #333;
          border-radius: 8px;
          padding: 20px;
          margin: 16px 0;
          overflow-x: auto;
          font-family: 'Fira Code', 'Courier New', monospace;
          font-size: 13px;
          line-height: 1.6;
        }

        .light .code-block{
            background-color: #efefef;
            border: none;
        }

        .code-block code {
          color: #d4d4d4;
          white-space: pre;
          display: block;
        }

        .light .code-block code{
            color : hsl(0 0% 62.8%);
            
        }

        .blog-text code {
          background-color: #2a2a2a;
          color: #e06c75;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Fira Code', 'Courier New', monospace;
          font-size: 0.9em;
        }

        .light .blog-text code{
            background-color: #d4d4d4;
            color: #a626a4;
        }
      `}</style>

      <div className="blog-content-in">
        <h3 className="heading">Building GitCheck: GitHub OAuth & API Integration Deep Dive</h3>

        <div className="subheading-content">
          <h4 className="subheading">Introduction:</h4>
          <p className="blog-text">
            GitCheck is a comprehensive GitHub profile analyzer that transforms code 
            contributions into quantifiable career metrics. Building this platform required 
            deep integration with GitHub's OAuth system and REST/GraphQL APIs. In this article, 
            I share the technical challenges, solutions, and key learnings from implementing 
            authentication, handling rate limits, and building a real-time analytics dashboard.
            <br /><br />
            <strong>Tech Stack:</strong> Next.js 14, NextAuth.js, GitHub API, PostgreSQL, Vercel
            <br />
            <strong>Live Demo:</strong> <a href="https://gitcheck.me" target="_blank" rel="noopener noreferrer">gitcheck.me</a>
            <br />
            <strong>GitHub:</strong> <a href="https://github.com/goktugkrblt/gitcheck" target="_blank" rel="noopener noreferrer">github.com/goktugkrblt/gitcheck</a>
          </p>
        </div>

        <div className="subheading-content">
          <h4 className="subheading">1. Setting Up GitHub OAuth with NextAuth.js:</h4>
          <p className="blog-text">
            The first challenge was implementing secure GitHub authentication. NextAuth.js 
            simplified this process significantly, but required careful configuration to work 
            properly in production.
            <br /><br />
            <strong>Initial Setup:</strong>
          </p>
          <pre className="code-block">
            <code>{`// lib/auth.ts
import { NextAuthOptions } from "next-auth";
import GithubProvider from "next-auth/providers/github";

export const authOptions: NextAuthOptions = {
  providers: [
    GithubProvider({
      clientId: process.env.GITHUB_CLIENT_ID!,
      clientSecret: process.env.GITHUB_CLIENT_SECRET!,
      authorization: {
        params: {
          scope: "read:user user:email repo",
        },
      },
    }),
  ],
  callbacks: {
    async signIn({ user, account, profile }) {
      // Store user in database
      const githubProfile = profile as any;
      await prisma.user.upsert({
        where: { githubId: githubProfile.id.toString() },
        update: {
          name: githubProfile.name,
          email: githubProfile.email,
          avatarUrl: githubProfile.avatar_url,
        },
        create: {
          githubId: githubProfile.id.toString(),
          githubUsername: githubProfile.login,
          name: githubProfile.name,
          email: githubProfile.email,
          avatarUrl: githubProfile.avatar_url,
        },
      });
      return true;
    },
  },
};`}</code>
          </pre>
          <p className="blog-text">
            <br />
            <strong>Key Challenge:</strong> The callback URL must match exactly between GitHub 
            OAuth App settings and your <code>NEXTAUTH_URL</code> environment variable. A 
            trailing slash mismatch caused hours of debugging!
          </p>
        </div>

        <div className="subheading-content">
          <h4 className="subheading">2. GitHub API Rate Limiting Strategy:</h4>
          <p className="blog-text">
            GitHub's API has a rate limit of 5,000 requests per hour for authenticated users. 
            With each profile analysis requiring 50-100 API calls, this became a critical 
            bottleneck.
            <br /><br />
            <strong>Problem:</strong>
            <br />
            • Fetching repositories: ~1 call per page (100 repos = multiple calls)
            <br />
            • Analyzing commits: 1 call per repository
            <br />
            • Fetching PRs and issues: 2+ calls per repository
            <br />
            • Result: Easy to hit rate limits with just 20-30 users
            <br /><br />
            <strong>Solution - Conditional Requests with ETags:</strong>
          </p>
          <pre className="code-block">
            <code>{`// lib/github.ts
export async function fetchWithCache(url: string, token: string) {
  const cached = await getCachedResponse(url);
  
  const headers: any = {
    Authorization: \`token \${token}\`,
    Accept: "application/vnd.github.v3+json",
  };
  
  // Add ETag if we have cached data
  if (cached?.etag) {
    headers["If-None-Match"] = cached.etag;
  }
  
  const response = await fetch(url, { headers });
  
  // 304 Not Modified - use cached data
  if (response.status === 304) {
    return cached.data;
  }
  
  // 403 Rate Limit Exceeded
  if (response.status === 403) {
    const resetTime = response.headers.get("X-RateLimit-Reset");
    throw new RateLimitError(\`Rate limit exceeded. Resets at \${resetTime}\`);
  }
  
  const data = await response.json();
  const etag = response.headers.get("ETag");
  
  // Cache for 1 hour
  await cacheResponse(url, data, etag, 3600);
  
  return data;
}`}</code>
          </pre>
          <p className="blog-text">
            <br />
            This approach reduced API calls by ~60% through intelligent caching.
          </p>
        </div>

        <div className="subheading-content">
          <h4 className="subheading">3. Combining REST and GraphQL APIs:</h4>
          <p className="blog-text">
            While GitHub's REST API handles most data fetching, the contribution graph 
            required GraphQL for efficient querying.
            <br /><br />
            <strong>GraphQL for Contribution Data:</strong>
          </p>
          <pre className="code-block">
            <code>{`// lib/github-graphql.ts
const CONTRIBUTIONS_QUERY = \`
  query($username: String!, $from: DateTime!, $to: DateTime!) {
    user(login: $username) {
      contributionsCollection(from: $from, to: $to) {
        contributionCalendar {
          totalContributions
          weeks {
            contributionDays {
              contributionCount
              date
            }
          }
        }
      }
    }
  }
\`;

export async function fetchContributions(username: string) {
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  
  const response = await fetch("https://api.github.com/graphql", {
    method: "POST",
    headers: {
      Authorization: \`Bearer \${process.env.GITHUB_TOKEN}\`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      query: CONTRIBUTIONS_QUERY,
      variables: {
        username,
        from: oneYearAgo.toISOString(),
        to: new Date().toISOString(),
      },
    }),
  });
  
  const { data } = await response.json();
  return data.user.contributionsCollection;
}`}</code>
          </pre>
          <p className="blog-text">
            <br />
            GraphQL reduced what would have been 365+ REST calls to a single query.
          </p>
        </div>

        <div className="subheading-content">
          <h4 className="subheading">4. Building the Scoring Algorithm:</h4>
          <p className="blog-text">
            The developer score is GitCheck's core feature. It analyzes 15+ metrics and 
            produces a 0-100 score with percentile ranking.
            <br /><br />
            <strong>Algorithm Breakdown:</strong>
          </p>
          <pre className="code-block">
            <code>{`// lib/scoring.ts
interface ScoringWeights {
  repository: 0.25;    // 25% - repos, stars, forks
  contribution: 0.30;  // 30% - commits, PRs, reviews
  community: 0.20;     // 20% - followers, organizations
  consistency: 0.15;   // 15% - streaks, frequency
  quality: 0.10;       // 10% - PR merge rate, diversity
}

export function calculateDevScore(profile: GitHubProfile): number {
  // Repository Score
  const repoScore = Math.min(100, (
    (profile.totalRepos * 2) +
    (profile.totalStars * 0.5) +
    (profile.totalForks * 0.3)
  ) / 10);
  
  // Contribution Score
  const contributionScore = Math.min(100, (
    (profile.totalCommits * 0.1) +
    (profile.mergedPRs * 5) +
    (profile.totalPRs * 2)
  ) / 10);
  
  // Community Score
  const communityScore = Math.min(100, (
    (profile.followersCount * 2) +
    (profile.organizationsCount * 10)
  ) / 5);
  
  // Consistency Score
  const consistencyScore = Math.min(100, (
    (profile.currentStreak * 3) +
    (profile.longestStreak * 1) +
    (profile.averageCommitsPerDay * 20)
  ));
  
  // Quality Score
  const qualityScore = Math.min(100, (
    (profile.mergedPRs / Math.max(1, profile.totalPRs) * 50) +
    (Object.keys(profile.languages).length * 5)
  ));
  
  // Weighted average
  const finalScore = (
    repoScore * 0.25 +
    contributionScore * 0.30 +
    communityScore * 0.20 +
    consistencyScore * 0.15 +
    qualityScore * 0.10
  );
  
  return Math.round(Math.min(100, finalScore));
}`}</code>
          </pre>
          <p className="blog-text">
            <br />
            <strong>Challenge:</strong> Handling outliers (mega-contributors vs beginners). 
            Solution: Applied logarithmic scaling and normalization to prevent score inflation.
          </p>
        </div>

        <div className="subheading-content">
          <h4 className="subheading">5. Database Schema Design:</h4>
          <p className="blog-text">
            PostgreSQL with Prisma ORM provided type-safe database operations. The schema 
            needed to balance between normalized data and query performance.
          </p>
          <pre className="code-block">
            <code>{`// prisma/schema.prisma
model User {
  id                    String   @id @default(cuid())
  githubId              String   @unique
  githubUsername        String   @unique
  email                 String?  @unique
  name                  String?
  avatarUrl             String?
  
  // Cached Metrics
  score                 Int      @default(0)
  percentile            Int      @default(0)
  totalRepos            Int      @default(0)
  totalStars            Int      @default(0)
  totalCommits          Int      @default(0)
  currentStreak         Int      @default(0)
  
  // JSON for complex data
  languages             Json     @default("{}")
  topRepos              Json     @default("[]")
  
  // Timestamps
  createdAt             DateTime @default(now())
  updatedAt             DateTime @updatedAt
  lastAnalyzedAt        DateTime?
  
  @@index([score])
  @@index([githubUsername])
}`}</code>
          </pre>
          <p className="blog-text">
            <br />
            <strong>Design Decision:</strong> Storing complex data (languages, top repos) as 
            JSON rather than separate tables simplified queries and reduced joins, improving 
            dashboard load times by 40%.
          </p>
        </div>

        <div className="subheading-content">
          <h4 className="subheading">6. Performance Optimizations:</h4>
          <p className="blog-text">
            Initial dashboard loads took 3-5 seconds. Through optimization, we reduced this 
            to under 800ms.
            <br /><br />
            <strong>Key Optimizations:</strong>
            <br />
            • <strong>Server Components:</strong> Next.js 14 App Router allowed us to fetch data 
            server-side, reducing client-side JavaScript by 60%
            <br />
            • <strong>Parallel Data Fetching:</strong> Using <code>Promise.all()</code> to fetch 
            multiple API endpoints simultaneously
            <br />
            • <strong>Image Optimization:</strong> Next.js Image component for avatar and repo images
            <br />
            • <strong>Incremental Static Regeneration:</strong> Cached user profiles with 1-hour revalidation
          </p>
          <pre className="code-block">
            <code>{`// app/dashboard/page.tsx
export const revalidate = 3600; // 1 hour

export default async function DashboardPage() {
  const session = await getServerSession(authOptions);
  
  // Parallel data fetching
  const [profile, repos, contributions] = await Promise.all([
    fetchProfile(session.user.username),
    fetchRepositories(session.user.username),
    fetchContributions(session.user.username),
  ]);
  
  return <Dashboard data={{ profile, repos, contributions }} />;
}`}</code>
          </pre>
        </div>

        <div className="subheading-content">
          <h4 className="subheading">7. Error Handling & User Experience:</h4>
          <p className="blog-text">
            Robust error handling was critical for production reliability. GitHub API failures, 
            rate limits, and network issues needed graceful degradation.
          </p>
          <pre className="code-block">
            <code>{`// lib/error-handler.ts
export class GitHubAPIError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public resetTime?: string
  ) {
    super(message);
    this.name = "GitHubAPIError";
  }
}

export async function handleAPIError(error: any) {
  if (error.statusCode === 403) {
    // Rate limit exceeded
    return {
      error: "Rate limit exceeded. Please try again later.",
      resetTime: error.resetTime,
    };
  }
  
  if (error.statusCode === 404) {
    return {
      error: "GitHub profile not found.",
    };
  }
  
  // Log to monitoring service
  console.error("GitHub API Error:", error);
  
  return {
    error: "Failed to fetch GitHub data. Please try again.",
  };
}`}</code>
          </pre>
          <p className="blog-text">
            <br />
            Users see helpful error messages instead of cryptic API failures, improving overall UX.
          </p>
        </div>

        <div className="subheading-content">
          <h4 className="subheading">8. Deployment & Production Considerations:</h4>
          <p className="blog-text">
            Deploying to Vercel was straightforward, but required careful environment variable 
            management and domain configuration.
            <br /><br />
            <strong>Key Production Setup:</strong>
            <br />
            • <strong>Environment Variables:</strong> <code>NEXTAUTH_URL</code>, <code>NEXTAUTH_SECRET</code>, 
            <code>GITHUB_CLIENT_ID</code>, <code>DATABASE_URL</code>
            <br />
            • <strong>OAuth Callbacks:</strong> Must match production domain exactly
            <br />
            • <strong>Database Connection Pooling:</strong> Neon's serverless Postgres handles 
            connection limits automatically
            <br />
            • <strong>Vercel Functions:</strong> Serverless API routes with automatic scaling
            <br /><br />
            <strong>Gotcha:</strong> Callback URL mismatches between development and production 
            caused authentication failures. Solution: Use separate OAuth apps for dev and prod.
          </p>
        </div>

        <div className="subheading-content">
          <h4 className="subheading">Key Takeaways:</h4>
          <p className="blog-text">
            <strong>What I Learned:</strong>
            <br /><br />
            <strong>1. Rate Limiting is Real:</strong> GitHub's API limits forced me to think 
            creatively about caching and conditional requests. ETags reduced unnecessary calls by 60%.
            <br /><br />
            <strong>2. OAuth Debugging Takes Time:</strong> Small misconfigurations (trailing slashes, 
            callback URLs) can cause hours of debugging. Always double-check production settings.
            <br /><br />
            <strong>3. GraphQL + REST = Best of Both Worlds:</strong> Using GraphQL for complex 
            queries and REST for simple fetches optimized both performance and developer experience.
            <br /><br />
            <strong>4. Server Components are Powerful:</strong> Next.js 14's App Router reduced 
            client-side JavaScript and improved performance significantly.
            <br /><br />
            <strong>5. Error Handling Matters:</strong> Graceful degradation and helpful error 
            messages dramatically improve user trust and retention.
          </p>
        </div>

        <div className="subheading-content">
          <h4 className="subheading">Future Improvements:</h4>
          <p className="blog-text">
            • <strong>WebSocket Integration:</strong> Real-time updates when users push new commits
            <br />
            • <strong>Team Analytics:</strong> Organization-level insights and comparisons
            <br />
            • <strong>Historical Tracking:</strong> Track score changes over time
            <br />
            • <strong>AI-Powered Insights:</strong> Personalized recommendations for improvement
            <br /><br />
            Building GitCheck taught me that production-ready applications require more than just 
            working code—they need thoughtful architecture, robust error handling, and constant 
            optimization. The combination of Next.js 14, GitHub's APIs, and modern deployment 
            platforms like Vercel makes building sophisticated web applications more accessible 
            than ever.
            <br /><br />
            <strong>Try GitCheck:</strong> <a href="https://gitcheck.me" target="_blank" rel="noopener noreferrer">gitcheck.me</a>
            <br />
            <strong>Source Code:</strong> <a href="https://github.com/goktugkrblt/gitcheck" target="_blank" rel="noopener noreferrer">github.com/goktugkrblt/gitcheck</a>
          </p>
        </div>
      </div>
    </>
  );
};

export default GitCheck;